import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminHead, FilterSelect } from "@/components/admin/AdminUI";
import { CategoryIcon } from "@/components/CategoryIcon";
import { PageLoader } from "@/components/layout/RequireAuth";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const QuestionEditor = ({ value, onChange }) => {
  const set = (i, patch) => onChange(value.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, d) => {
    const arr = [...value];
    [arr[i], arr[i + d]] = [arr[i + d], arr[i]];
    onChange(arr);
  };
  return (
    <div className="space-y-3" data-testid="question-editor">
      {value.map((q, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-stone-200 p-3" data-testid={`qe-item-${i}`}>
          <div className="flex gap-2">
            <Input value={q.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Pertanyaan" className="rounded-lg" data-testid={`qe-label-${i}`} />
            <FilterSelect value={q.type} onChange={(v) => set(i, { type: v })} testId={`qe-type-${i}`} options={[["pilihan", "Pilihan"], ["angka", "Angka"], ["teks", "Teks"]]} />
          </div>
          {q.type === "pilihan" && (
            <Input value={(q.options || []).join(", ")} onChange={(e) => set(i, { options: e.target.value.split(",").map((s) => s.trimStart()) })}
              placeholder="Pilihan, pisahkan dengan koma" className="rounded-lg" data-testid={`qe-options-${i}`} />
          )}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={q.required} onCheckedChange={(v) => set(i, { required: v })} data-testid={`qe-required-${i}`} /> Wajib</label>
            <div className="flex gap-1">
              <Button type="button" size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`qe-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="ghost" disabled={i === value.length - 1} onClick={() => move(i, 1)} data-testid={`qe-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`qe-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-lg" data-testid="qe-add"
        onClick={() => onChange([...value, { key: "", label: "", type: "teks", options: [], required: false }])}>
        <Plus className="mr-1 h-4 w-4" /> Tambah pertanyaan
      </Button>
    </div>
  );
};

const CategoryForm = ({ initial, groups, onSaved }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...f, questions: f.questions.map((q) => ({ ...q, options: (q.options || []).map((o) => o.trim()).filter(Boolean) })) };
    try {
      if (f.slug) await api.put(`/admin/catalog/categories/${f.slug}`, payload);
      else await api.post("/admin/catalog/categories", payload);
      toast.success("Subkategori disimpan");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="category-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Nama"><Input required value={f.name} onChange={set("name")} className="rounded-lg" data-testid="cat-name-input" /></F>
        <F label="Grup"><FilterSelect value={f.group_slug} onChange={set("group_slug")} testId="cat-group-select" options={groups.map((g) => [g.slug, g.name])} /></F>
        <F label="Ikon (nama lucide, mis. Wrench)"><Input value={f.icon} onChange={set("icon")} className="rounded-lg" data-testid="cat-icon-input" /></F>
        <F label="URL gambar (opsional)"><Input value={f.image || ""} onChange={set("image")} className="rounded-lg" data-testid="cat-image-input" /></F>
      </div>
      <F label="Deskripsi"><Textarea required rows={2} value={f.description} onChange={set("description")} className="rounded-lg" data-testid="cat-description-input" /></F>
      <F label="Kata kunci pencarian"><Input value={f.keywords || ""} onChange={set("keywords")} className="rounded-lg" data-testid="cat-keywords-input" /></F>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.active !== false} onCheckedChange={set("active")} data-testid="cat-active-switch" /> Aktif</label>
      <div><p className="mb-2 text-sm font-bold text-stone-900">Pertanyaan formulir</p><QuestionEditor value={f.questions} onChange={set("questions")} /></div>
      <Button type="submit" className="w-full rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="cat-save-button">Simpan</Button>
    </form>
  );
};

const GroupForm = ({ initial, onSaved }) => {
  const [f, setF] = useState(initial);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (f.slug) await api.put(`/admin/catalog/groups/${f.slug}`, f);
      else await api.post("/admin/catalog/groups", f);
      toast.success("Grup disimpan");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="group-form">
      <F label="Nama grup"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-lg" data-testid="group-name-input" /></F>
      <F label="Deskripsi"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-lg" data-testid="group-description-input" /></F>
      <F label="URL gambar"><Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className="rounded-lg" data-testid="group-image-input" /></F>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.active !== false} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="group-active-switch" /> Aktif</label>
      <Button type="submit" className="w-full rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="group-save-button">Simpan</Button>
    </form>
  );
};

const Cities = ({ cities, reload }) => {
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post("/admin/catalog/cities", { name, active: true }); setName(""); reload(); toast.success("Kota ditambahkan"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c) => { await api.put(`/admin/catalog/cities/${c.slug}`, { name: c.name, active: c.active === false }); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex max-w-md gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama kota baru" className="rounded-xl" data-testid="city-name-input" />
        <Button onClick={add} disabled={name.trim().length < 2} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="city-add-button">Tambah</Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {cities.map((c) => (
          <div key={c.slug} className="card-soft flex items-center justify-between px-4 py-3" data-testid={`admin-city-${c.slug}`}>
            <span className={c.active === false ? "text-stone-400 line-through" : "font-medium text-stone-800"}>{c.name}</span>
            <Switch checked={c.active !== false} onCheckedChange={() => toggle(c)} data-testid={`city-toggle-${c.slug}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminCatalog() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = () => api.get("/admin/catalog").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <PageLoader />;
  const done = () => { setDialog(null); load(); };
  const toggleCat = async (s) => {
    try { await api.put(`/admin/catalog/categories/${s.slug}`, { ...s, active: s.active === false }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-catalog">
      <AdminHead title="Kategori & Kota" desc="Subkategori nonaktif disembunyikan dari halaman publik dan formulir permintaan; data lama tetap aman.">
        <Button onClick={() => setDialog({ type: "group", data: { name: "", description: "", image: "", active: true } })} variant="outline" className="rounded-xl" data-testid="add-group-button"><Plus className="mr-1 h-4 w-4" /> Grup</Button>
        <Button onClick={() => setDialog({ type: "cat", data: { name: "", group_slug: data.groups[0]?.slug, icon: "Briefcase", description: "", image: "", keywords: "", active: true, questions: [] } })} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="add-category-button"><Plus className="mr-1 h-4 w-4" /> Subkategori</Button>
      </AdminHead>
      <Tabs defaultValue="kategori">
        <TabsList className="rounded-full bg-stone-100 p-1">
          <TabsTrigger value="kategori" className="rounded-full px-4" data-testid="catalog-tab-categories">Kategori</TabsTrigger>
          <TabsTrigger value="kota" className="rounded-full px-4" data-testid="catalog-tab-cities">Kota</TabsTrigger>
        </TabsList>
        <TabsContent value="kategori" className="mt-6 space-y-6">
          {data.groups.map((g) => (
            <div key={g.slug} className={`card-soft p-5 ${g.active === false ? "opacity-60" : ""}`} data-testid={`admin-group-${g.slug}`}>
              <div className="flex items-center justify-between gap-3">
                <div><p className="font-bold text-stone-900">{g.name} {g.active === false && <span className="text-xs text-rose-600">(nonaktif)</span>}</p><p className="text-xs text-stone-500">{g.description}</p></div>
                <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "group", data: g })} data-testid={`edit-group-${g.slug}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Ubah</Button>
              </div>
              <div className="mt-4 divide-y divide-stone-100">
                {g.subcategories.map((s) => (
                  <div key={s.slug} className="flex items-center gap-3 py-2.5" data-testid={`admin-category-${s.slug}`}>
                    <CategoryIcon name={s.icon} className="h-4 w-4 text-teal-700" />
                    <span className={`flex-1 text-sm ${s.active === false ? "text-stone-400 line-through" : "font-medium text-stone-800"}`}>{s.name}</span>
                    <span className="text-xs text-stone-400">{s.questions.length} pertanyaan</span>
                    <Switch checked={s.active !== false} onCheckedChange={() => toggleCat(s)} data-testid={`category-toggle-${s.slug}`} />
                    <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "cat", data: s })} data-testid={`edit-category-${s.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="kota" className="mt-6"><Cities cities={data.cities} reload={load} /></TabsContent>
      </Tabs>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle>{dialog?.type === "group" ? "Grup kategori" : "Subkategori"}</DialogTitle></DialogHeader>
          {dialog?.type === "group" && <GroupForm initial={dialog.data} onSaved={done} />}
          {dialog?.type === "cat" && <CategoryForm initial={dialog.data} groups={data.groups} onSaved={done} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
